import React, { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import meta from '../../config/meta';
import { env } from '../../config';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import Html from '~/commons/components/Html.jsx';
import PageHttp from '~/commons/components/PageHttp.jsx';

// Segmented circular progress SVG component
function polarToCartesian(cx, cy, r, angle) {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}
const CircleProgress = ({
  total = 0,
  finished = 0,
  color,
  size = 64,
  strokeWidth = 8,
}) => {
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const grey = '#E0E0E0';

  // Handle case when total is 0 - show a simple grey circle
  if (total === 0) {
    return (
      <svg width={size} height={size} style={{ display: 'block' }}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={grey}
          strokeWidth={strokeWidth}
        />
        {/* Center text */}
        <text
          x={center}
          y={center + 6}
          textAnchor="middle"
          fontSize={size * 0.28}
          fontWeight="bold"
          fill={color}
        >
          {finished}篇
        </text>
      </svg>
    );
  }

  const anglePer = 360 / total;
  // Helper to describe an arc for each segment
  const describeArc = (startAngle, endAngle) => {
    const start = polarToCartesian(center, center, radius, endAngle);
    const end = polarToCartesian(center, center, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [
      'M',
      start.x,
      start.y,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
    ].join(' ');
  };

  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      {/* Draw each segment: finished in color, rest in grey */}
      {Array.from({ length: total }).map((_, i) => {
        const startAngle = i * anglePer;
        const endAngle = (i + 1) * anglePer;
        const isFinished = i < finished;
        // Calculate line start/end for segment head
        const lineLength = radius + strokeWidth / 2;
        const lineStart = polarToCartesian(
          center,
          center,
          radius - strokeWidth / 2,
          startAngle,
        );
        const lineEnd = polarToCartesian(
          center,
          center,
          lineLength,
          startAngle,
        );
        return (
          <g key={`seg-${i}`}>
            <path
              d={describeArc(startAngle, endAngle)}
              fill="none"
              stroke={isFinished ? color : grey}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
            />
            {/* Segment head line */}
            <line
              x1={lineStart.x}
              y1={lineStart.y}
              x2={lineEnd.x}
              y2={lineEnd.y}
              stroke="#B0B0B0"
              strokeWidth={2}
            />
          </g>
        );
      })}
      {/* Center text */}
      <text
        x={center}
        y={center + 6}
        textAnchor="middle"
        fontSize={size * 0.28}
        fontWeight="bold"
        fill={color}
      >
        {finished}篇
      </text>
    </svg>
  );
};

const PeriodSection = ({
  r, i, today, answer,
}) => {
  // Color mapping for each period
  const periodColors = ['#008A48', '#934403', '#A21D1D'];
  const disabledColor = '#6D6D6D';
  let borderClass = 'period-inactive-border';
  let color = disabledColor;
  let isActive = false;
  let isAfterPeriod = false;
  let isBeforePeriod = false;
  let isWithinPeriod = false;
  if (r.startDate && r.endDate) {
    const start = new Date(r.startDate);
    const end = new Date(r.endDate);
    isAfterPeriod = today > end;
    isBeforePeriod = today < start;
    isWithinPeriod = today >= start && today <= end;
    if (isWithinPeriod || isAfterPeriod) {
      borderClass = `period-${i + 1}-border`;
      color = periodColors[i] || disabledColor;
      isActive = true;
    } else {
      borderClass = 'period-inactive-border';
      color = disabledColor;
      isActive = false;
    }
  }
  const levelMap = {
    0: '第一階段',
    1: '第二階段',
    2: '第三階段',
  };
  // Calculate finished count and total for the circle
  const total = Array.isArray(answer) ? answer.length : 0;
  const finished = Array.isArray(answer)
    ? answer.filter((a) => a.status === 'completed').length
    : 0;
  return (
    <Link
      to={isActive ? `/app/missions/${r._id}` : '/app'} // Disable link if not active
      className={`${borderClass}`}
      style={{
        minHeight: '200px',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        textDecoration: 'none',
        pointerEvents: isBeforePeriod ? 'none' : 'auto',
        opacity: isBeforePeriod ? 0.5 : 1,
      }}
    >
      <div
        className="period-content"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <div
          className="text-center period-title"
          style={{
            fontSize: '24px',
            color,
            fontWeight: 'bold',
          }}
        >
          {levelMap[i]}
        </div>

        <div
          className="text-center period-date"
          style={{
            fontSize: '16px',
            color,
            minWidth: '200px',
            display: 'inline-block',
          }}
        >
          {(() => {
            if (r.startDate && r.endDate) {
              const start = new Date(r.startDate);
              const end = new Date(r.endDate);
              if (isAfterPeriod) {
                return '階段已結束';
              }
              // Show date for within or before period
              return `${start.getFullYear()}/${
                start.getMonth() + 1
              }/${start.getDate()}-${end.getFullYear()}/${
                end.getMonth() + 1
              }/${end.getDate()}`;
            }
            return '';
          })()}
        </div>
        {isActive ? (
          <>
            <div className="d-flex align-items-center justify-content-center" style={{ gap: 6, margin: '8px 0' }}>
              <span
                className="completion-text"
                style={{
                  fontSize: '16px',
                  color: '#333333',
                  fontWeight: '700',
                }}
              >
                完成
              </span>
              <CircleProgress
                total={total || 0}
                finished={finished || 0}
                color={color}
                size={64}
              />
            </div>
            <div className="d-flex align-items-center justify-content-center" style={{ gap: 4, flexWrap: 'wrap' }}>
              <span
                className="difficulty-text"
                style={{
                  fontSize: '14px',
                  color: '#333333',
                  fontWeight: '700',
                }}
              >
                難度
              </span>
              <div className="d-flex align-items-center" style={{ gap: 2, marginLeft: 4 }}>
                {[0, 1, 2].map((starIdx) => (
                  <svg
                    key={starIdx}
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0 }}
                  >
                    <polygon
                      points="10,2 12.472,7.236 18,7.854 13.8,11.764 15.09,17.146 10,14.1 4.91,17.146 6.2,11.764 2,7.854 7.528,7.236"
                      fill={starIdx <= i ? '#FFD600' : '#E0E0E0'}
                      stroke="#B0B0B0"
                      strokeWidth="1"
                    />
                  </svg>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 40 }}
            >
              <span
                style={{
                  fontSize: '16px',
                  color: '#999',
                  fontWeight: '700',
                }}
              >
                答題暫未開始
              </span>
            </div>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 32 }}
            >
              {/* Lock icon centered */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="5"
                  y="11"
                  width="14"
                  height="8"
                  rx="2"
                  fill="#E0E0E0"
                  stroke="#B0B0B0"
                  strokeWidth="1.5"
                />
                <path
                  d="M8 11V8a4 4 0 1 1 8 0v3"
                  stroke="#B0B0B0"
                  strokeWidth="1.5"
                  fill="none"
                />
                <circle cx="12" cy="15" r="1.5" fill="#B0B0B0" />
              </svg>
            </div>
          </>
        )}
      </div>
    </Link>
  );
};

const PageDashboard = () => {
  const [slotRes, slotReq] = useHttpStatus();
  const [assignmentRes, assignmentReq, fetched = { total: 0 }] = useHttpStatus();
  const [answerRes, answerReq] = useHttpStatus();
  const history = useHistory();

  meta.set();
  useEffect(() => {
    slotReq({ url: '/api/site/view-slots/app/select-stage' });
    assignmentReq({
      url: '/api/quiz/assignments',
      params: {
        sort: { createdAt: 1 },
        noArticles: true,
      },
    });
    answerReq({ url: '/api/quiz/answers/grouped-answers' });
  }, []);

  // Check if all periods are finished and redirect to reading history
  useEffect(() => {
    if (fetched?.rows && answerRes.payload) {
      // Check global review window from server-side setting
      let withinGlobalReview = false;
      if (env && env.certSetting) {
        const { reviewOpenDate, reviewCloseDate } = env.certSetting;
        const rOpenMs = reviewOpenDate ? Date.parse(reviewOpenDate) : NaN;
        const rCloseMs = reviewCloseDate ? Date.parse(reviewCloseDate) : NaN;
        // Log minimal info to help debug in browser console
        if (!Number.isNaN(rOpenMs) && !Number.isNaN(rCloseMs)) {
          const now = Date.now();
          withinGlobalReview = now >= rOpenMs && now <= rCloseMs;
        }
      }
      if (withinGlobalReview) {
        history.replace('/app/reading-history');
      }
      // if (true) {
      //   // Redirect to reading history page
      //   history.replace('/app/reading-history');
      // }
    }
  }, [assignmentRes, answerRes, history, fetched]);

  const res = [slotRes, assignmentRes, answerRes];

  return (
    <PageHttp res={res}>
      {([slot, , answers]) => {
        const today = new Date();
        return (
          <>
            <div
              className="container-fluid p-0 position-relative d-flex flex-column flex-lg-row"
              style={{
                background: '#fff9f1',
                minHeight: '100vh',
                alignItems: 'center',
                justifyContent: 'center',
                overflowX: 'hidden',
              }}
            >
              <div className="row justify-content-between">
                <div className="col d-none d-lg-flex justify-content-center">
                  <img
                    src="/assets/img/boy.svg"
                    alt="boy"
                    className="d-block"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </div>
                <div className="col-auto">
                  <div className="px-3 px-md-0">
                    <div className="d-flex justify-content-center mb-4">
                      <span
                        style={{
                          fontWeight: 800,
                          fontSize: 44,
                          color: '#542600',
                        }}
                      >
                        任務階段
                      </span>
                    </div>
                    <Html>{slot.content}</Html>
                  </div>
                  <div className="row justify-content-between my-3 px-2 px-md-0 mobile-margin-x">
                    {fetched.rows.map((r, i) => (
                      <div
                        className="col-12 col-md mb-3 mb-md-0"
                        key={i}
                        style={{ minWidth: 0, display: 'flex' }}
                      >
                        <div
                        className='period-box'
                        style={{
                          aspectRatio: '1 / 1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <PeriodSection
                            r={r}
                            i={i}
                            today={today}
                            answer={
                              answers && answers[`period${i + 1}`]
                                ? answers[`period${i + 1}`]
                                : []
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col d-none d-lg-flex justify-content-center">
                  <img
                    src="/assets/img/girl.svg"
                    alt="girl"
                    className="d-block"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>
              <div
                className="d-md-none text-center mt-4"
                style={{ fontSize: '14px', color: '#666' }}
              >
                *相關階段結束後，不能作答
              </div>
              <div
                className="position-absolute d-none d-md-block"
                style={{
                  bottom: '10px',
                  right: '10px',
                  fontSize: '12px',
                  color: '#666',
                  background: 'rgba(255, 249, 241, 0.9)',
                  padding: '5px 10px',
                  borderRadius: '5px',
                }}
              >
                *相關階段結束後，不能作答
              </div>
            </div>
          </>
        );
      }}
    </PageHttp>
  );
};

export default PageDashboard;
